import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, rectSortingStrategy, SortableContext, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { cloneDeep } from 'es-toolkit';

import { Box, List, Paragraph, Spinner, Toast, useToast } from '@zillow/constellation-10';

import { PhotoDetailsModal } from '@zg-rentals/maintenance-utils';
import { useQueryClient } from '@zg-rentals/react-query5';
import {
  useRentalManagerUserPropertyFloorPlanPhotoV2WebApiHandler_caption,
  useRentalManagerUserPropertyFloorPlanPhotoV2WebApiHandler_delete,
  useRentalManagerUserPropertyFloorPlanPhotoWebApiHandler_deleteForFeed,
  useRentalManagerUserPropertyMultiplexWebApiHandler_details,
  useRentalManagerUserPropertyPhotoV2WebApiHandler_caption,
  useRentalManagerUserPropertyPhotoV2WebApiHandler_delete,
  useRentalManagerUserPropertyPhotoV2WebApiHandler_rank,
  useRentalManagerUserPropertyPhotoWebApiHandler_reorderForFeed,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { getPhotoUrls } from '@zg-rentals/zillow-rental-manager/utils';

import {
  useIsOnReviewStep,
  useMultiplexBuildingSessionId,
  useMultiplexDetails,
  useMultiplexListingFlow,
  useMultiplexPhotoCaption,
  useMultiplexPhotoDelete,
  useMultiplexPhotoRank,
} from '../../../../../hooks/queries/multiplex';
import { useGetListingDetails } from '../../../../../hooks/queries/properties';
import { useAiImageListingCompliance } from '../../../../../hooks/queries/useAiImageListingCompliance';
import { useFeedPhotoUploadLock } from '../../../../../hooks/useFeedPhotoUploadLock';
import { useIsPhotoUploadRestricted } from '../../../../../hooks/useIsPhotoUploadRestricted';
import { usePropertyEditMutation } from '../../../../../hooks/usePropertyEditMutation';
import { useReimportLock } from '../../../../../hooks/useReimportLock';
import { getFeedFloorPlanPhotoDeleteCheck } from '../../../../../utils/feedFloorPlanPhotoUtils';

import { PHOTO_UPLOAD_FROM_COMPUTER_TEXT } from '../../../../../constants/PhotoUploadConstants';

import DiscriminatoryContentErrorAlert from '../../../../../components/PhotoGallery/components/DiscriminatoryContentErrorAlert';
import SortableItem from '../../../../../components/PhotoGallery/components/SortableItem';

import { useAuth } from '../../../../AuthProvider';
import FeedPhotoUploadPausedMessage from './FeedPhotoUploadPausedMessage';
import PhotoUploadDropzone from './PhotoUpload';

import type { DragEndEvent } from '@dnd-kit/core';
import type { Dispatch, ReactNode, SetStateAction } from 'react';
import type { PhotoProps } from '@zg-rentals/maintenance-utils';

type PhotoContext =
  | { type: 'property'; listingAlias: string }
  | { type: 'floorPlan'; floorPlanId: string; floorPlanIndex: number };

type Props = {
  photoContext: PhotoContext;
  uploadingCount: number;
  setUploadingCount: Dispatch<SetStateAction<number>>;
  /** Passed from PhotoUploadFormField so parent can sync multiplex/listing photos and optimistic uploads. */
  photos?: Array<PhotoProps>;
  setPhotos?: Dispatch<SetStateAction<Array<PhotoProps>>>;
  /** Forwards successful uploads to parent state (floor-plan usage omits this). */
  onUploadSuccess?: (uploadedPhotos: Array<PhotoProps>) => void;
  /** Clears pending-upload tracking when a photo is removed (PhotoUploadFormField only). */
  onPhotoDeleted?: (photoId: string) => void;
  /** Whether the add-photo affordance renders; the parent owns the feed/section rules for its context. */
  isUploadAllowed: boolean;
  /** Rendered as the first grid tile, left of the upload dropzone. */
  phoneUploadTrigger?: ReactNode;
};

const DEFAULT_CAPTION_SAVE_ERROR_MESSAGE = 'Caption couldn’t be saved. Try again.';

type CaptionSaveUpdateContext = {
  photos: Array<PhotoProps>;
  photoId: string;
  caption: string;
  setPhotos: Dispatch<SetStateAction<Array<PhotoProps>>>;
  discriminatoryContentErrors: Array<{ photoId: string; caption: string }>;
  setDiscriminatoryContentErrors: Dispatch<SetStateAction<Array<{ photoId: string; caption: string }>>>;
};

const applyCaptionSaveError = (context: CaptionSaveUpdateContext, errorMessage?: string): void => {
  const { photos, photoId, caption, setPhotos, discriminatoryContentErrors, setDiscriminatoryContentErrors } = context;
  const displayError = errorMessage || DEFAULT_CAPTION_SAVE_ERROR_MESSAGE;
  const copyPhotos = cloneDeep(photos);
  const indexToUpdate = copyPhotos.findIndex((photo) => photo.id === photoId);

  if (indexToUpdate === -1) {
    return;
  }

  copyPhotos[indexToUpdate].error = displayError;
  copyPhotos[indexToUpdate].caption = caption;
  setPhotos(copyPhotos);

  if (errorMessage?.includes('Good Neighbor Policy')) {
    setDiscriminatoryContentErrors([...discriminatoryContentErrors, { photoId, caption }]);
  }
};

const applyCaptionSaveSuccess = (
  context: Pick<CaptionSaveUpdateContext, 'photos' | 'photoId' | 'caption' | 'setPhotos'>,
): void => {
  const { photos, photoId, caption, setPhotos } = context;
  const copyPhotos = cloneDeep(photos);
  const indexToUpdate = copyPhotos.findIndex((photo) => photo.id === photoId);

  if (indexToUpdate === -1) {
    return;
  }

  copyPhotos[indexToUpdate].caption = caption;
  copyPhotos[indexToUpdate].error = undefined;
  setPhotos(copyPhotos);
};

const PhotoGallery = ({
  photoContext,
  uploadingCount,
  setUploadingCount,
  photos: photosProp,
  setPhotos: setPhotosProp,
  onUploadSuccess,
  onPhotoDeleted,
  isUploadAllowed,
  phoneUploadTrigger,
}: Props) => {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueToast } = useToast();
  const queryClient = useQueryClient();

  const listingAlias =
    photoContext.type === 'property' ? photoContext.listingAlias : (router.query.listingAlias as string);

  const isOnReviewStep = useIsOnReviewStep();

  const { data: listingDetails } = useGetListingDetails(listingAlias);
  const { data: multiplexData } = useMultiplexDetails({
    enabled: !isOnReviewStep,
  });

  const [internalPhotos, setInternalPhotos] = useState<Array<PhotoProps>>([]);
  const photos = photosProp ?? internalPhotos;
  const setPhotos = setPhotosProp ?? setInternalPhotos;
  /** PhotoUploadFormField supplies photos/setPhotos; floor plans use internal state only. */
  const isControlled = photosProp != null && setPhotosProp != null;
  const [clickedPhotoIdx, setClickedPhotoIdx] = useState<number | undefined | null>();
  const [discriminatoryContentErrors, setDiscriminatoryContentErrors] = useState<
    Array<{ photoId: string; caption: string }>
  >([]);

  const isMultifamily = listingDetails?.isMultifamily ?? false;
  const isFeed = listingDetails?.isFeed ?? false;
  const isArchived = listingDetails?.isArchived ?? false;
  const isUpdateListingRestricted = listingDetails?.restrictions?.isUpdateListingRestricted ?? false;
  const isPhotoUploadRestricted = useIsPhotoUploadRestricted(listingAlias);
  const { isEnabled: isAiImageComplianceEnabled } = useAiImageListingCompliance({ listingAlias });

  const canUpdateListing = user?.features?.canUpdateListing ?? false;
  const isRunningInProd = process.env.NODE_ENV === 'production';

  const { isInMultiplexListingFlow } = useMultiplexListingFlow();
  const { multiplexBuildingSessionId } = useMultiplexBuildingSessionId();
  /**
   * Multiplex building-session APIs for sort/delete/caption on property photos during the wizard.
   * On review (`?step=review`), use per-listing photo APIs instead.
   */
  const saveViaMultiplex =
    photoContext.type === 'property' && isOnReviewStep === false && Boolean(isInMultiplexListingFlow);

  const { mutateAsync: multiplexDeletePhotoAsync } = useMultiplexPhotoDelete({
    meta: {
      invalidates: ['getMultiplexUnits', 'getPropertyTableUnits', 'getGroupableProperties'],
      context: {
        api: 'useMultiplexPhotoDelete',
        details: 'PhotoGallery',
      },
    },
  });

  const { mutateAsync: multiplexSortPhotoAsync } = useMultiplexPhotoRank({
    meta: {
      invalidates: ['getMultiplexUnits', 'getPropertyTableUnits', 'getGroupableProperties'],
      context: {
        api: 'useMultiplexPhotoRank',
        details: 'PhotoGallery',
      },
    },
  });

  const { mutateAsync: multiplexPhotoCaptionAsync } = useMultiplexPhotoCaption({
    meta: {
      invalidates: ['getMultiplexUnits', 'getPropertyTableUnits', 'getGroupableProperties'],
      context: {
        api: 'useMultiplexPhotoCaption',
        details: 'PhotoGallery',
      },
    },
  });

  // Feed photo order is a manual override keyed by each photo's source URL, so reordering stays locked until every
  // photo has resolved a source (a just-uploaded photo hasn't yet) and the listing allows media overrides.
  const allPhotosHaveSource = photos.every((photo) => Boolean(photo.source));
  const { mutateAsync: reorderFeedPhotos, isPending: isFeedReorderPending } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyPhotoWebApiHandler_reorderForFeed,
    skipToast: true,
    meta: {
      invalidates: ['getMultiplexUnits', 'getPropertyTableUnits', 'getGroupableProperties'],
      context: { api: 'RentalManagerUserPropertyPhotoWebApiHandler_reorderForFeed' },
    },
  });
  const { isReimportLocked } = useReimportLock({ listingAlias });
  const { isFeedPhotoUploadLocked } = useFeedPhotoUploadLock({ listingAlias, scope: photoContext });
  const isFeedReorderLocked =
    isFeed &&
    (!isUploadAllowed || !allPhotosHaveSource || isFeedPhotoUploadLocked || isReimportLocked || isFeedReorderPending);
  const isDragDisabled = photoContext.type === 'floorPlan' || isFeedReorderLocked;

  const { mutateAsync: sortPhotos } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyPhotoV2WebApiHandler_rank,
    skipToast: true,
    meta: {
      invalidates: ['getMultiplexUnits', 'getPropertyTableUnits', 'getGroupableProperties'],
      context: { api: 'RentalManagerUserPropertyPhotoV2WebApiHandler_rank' },
    },
  });

  // Each feed reorder gets a token; a late failure only rolls back if no newer reorder has superseded it.
  const feedReorderTokenRef = useRef(0);

  const { mutateAsync: deletePropertyPhoto } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyPhotoV2WebApiHandler_delete,
    successToastMessage: 'Photo deleted',
    meta: {
      invalidates: ['getMultiplexUnits', 'getPropertyTableUnits', 'getGroupableProperties'],
      context: { api: 'RentalManagerUserPropertyPhotoV2WebApiHandler_delete' },
    },
  });

  const { mutateAsync: deleteFloorPlanPhoto } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyFloorPlanPhotoV2WebApiHandler_delete,
    successToastMessage: 'Photo deleted',
    meta: { context: { api: 'RentalManagerUserPropertyFloorPlanPhotoV2WebApiHandler_delete' } },
  });

  const { mutateAsync: deleteFeedFloorPlanPhoto } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyFloorPlanPhotoWebApiHandler_deleteForFeed,
    successToastMessage: 'Photo deleted',
    meta: { context: { api: 'RentalManagerUserPropertyFloorPlanPhotoWebApiHandler_deleteForFeed' } },
  });

  const { mutateAsync: updatePropertyPhotoCaption } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyPhotoV2WebApiHandler_caption,
    skipToast: true,
    meta: { context: { api: 'RentalManagerUserPropertyPhotoV2WebApiHandler_caption' } },
  });

  const { mutateAsync: updateFloorPlanPhotoCaption } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyFloorPlanPhotoV2WebApiHandler_caption,
    skipToast: true,
    meta: { context: { api: 'RentalManagerUserPropertyFloorPlanPhotoV2WebApiHandler_caption' } },
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        // hack to disable drag and drop till the photo is moved greater than 3000px which is not common on desktop
        distance: isDragDisabled ? 3000 : 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        distance: isDragDisabled ? 3000 : 5,
      },
    }),
  );

  /** Parent (PhotoUploadFormField) owns photo sync when controlled; floor plans load from listing here. */
  useEffect(() => {
    if (isControlled) {
      return;
    }

    const sourcePhotos =
      photoContext.type === 'property'
        ? isInMultiplexListingFlow && !isOnReviewStep
          ? multiplexData?.propertyPhotos
          : listingDetails?.propertyPhotos
        : listingDetails?.floorPlans?.[photoContext.floorPlanIndex]?.floorPlanPhotos;

    if (sourcePhotos != null) {
      setPhotos(sourcePhotos);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [isControlled, photoContext, listingDetails?.propertyPhotos, listingDetails?.floorPlans, setPhotos]);

  const handleDragEnd = async (event: DragEndEvent) => {
    // The pointer/touch distance hack doesn't stop the keyboard sensor, so guard the persist here too: a locked
    // gallery (floor plan, or a feed listing without override access / with unresolved sources) must not reorder.
    if (uploadingCount || isDragDisabled) {
      return;
    }

    const { active, over } = event;

    // `over` is null when the item is dropped outside a valid sortable target; only reorder real item-to-item drops.
    if (active.id === over?.id || !over) {
      return;
    }

    const oldIndex = photos.findIndex((item) => item.id === active.id);
    const newIndex = photos.findIndex((item) => item.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Compute the next order up front and hand setPhotos a plain value; the updater must stay pure (Strict Mode runs
    // it twice in dev), so the reorder mutation fires here, outside it.
    const previousPhotos = photos;
    const sortedArray: Array<PhotoProps> = arrayMove(photos, oldIndex, newIndex);
    const sortedArraysWithUpdatedRank = sortedArray.map((photo, index) => ({ ...photo, rank: index + 1 }));

    if (isFeed) {
      const orderedSources = sortedArray
        .map((photo) => photo.source)
        .filter((source): source is string => Boolean(source));

      // Every photo must contribute a source or the override would drop photos; bail without reordering.
      if (orderedSources.length !== sortedArray.length) {
        return;
      }

      setPhotos(sortedArraysWithUpdatedRank);

      const reorderToken = ++feedReorderTokenRef.current;

      void reorderFeedPhotos({ postData: { listingAlias, orderedSources } }).catch(() => {
        // A newer drag already superseded this one; don't clobber its order with this stale rollback.
        if (feedReorderTokenRef.current !== reorderToken) {
          return;
        }
        setPhotos(previousPhotos);
        enqueueToast(<Toast tone="critical">Photos couldn’t be reordered. Refresh and try again.</Toast>);
      });

      return;
    }

    setPhotos(sortedArraysWithUpdatedRank);

    const ranksPayload = sortedArraysWithUpdatedRank.map((p) => ({ id: p.id, rank: String(p.rank) }));
    const mutationFunc = saveViaMultiplex ? multiplexSortPhotoAsync : sortPhotos;
    let previousMultiplexData: typeof multiplexData;

    if (saveViaMultiplex) {
      // Keep the active multiplex details cache aligned with the optimistic dropped order.
      queryClient.setQueryData(
        useRentalManagerUserPropertyMultiplexWebApiHandler_details.getKey({
          multiplexBuildingSessionId: multiplexBuildingSessionId as string,
        }),
        (oldData: typeof multiplexData) => {
          previousMultiplexData = oldData;
          return oldData ? { ...oldData, propertyPhotos: sortedArraysWithUpdatedRank } : oldData;
        },
      );
    }

    const reorderPromise = saveViaMultiplex
      ? (mutationFunc as typeof multiplexSortPhotoAsync)({
          postData: { multiplexBuildingSessionId: multiplexBuildingSessionId as string, ranks: ranksPayload },
        })
      : (mutationFunc as typeof sortPhotos)({
          postData: { listingAlias, ranks: ranksPayload },
        });

    void reorderPromise
      .then(() => {
        if (saveViaMultiplex) {
          void queryClient.invalidateQueries({
            queryKey: useRentalManagerUserPropertyMultiplexWebApiHandler_details.getKey({
              multiplexBuildingSessionId: multiplexBuildingSessionId as string,
            }),
            refetchType: 'inactive',
          });
        }
      })
      .catch(() => {
        if (saveViaMultiplex && previousMultiplexData) {
          queryClient.setQueryData(
            useRentalManagerUserPropertyMultiplexWebApiHandler_details.getKey({
              multiplexBuildingSessionId: multiplexBuildingSessionId as string,
            }),
            previousMultiplexData,
          );
        }

        enqueueToast(<Toast tone="critical">Photos couldn’t be reordered. Refresh and try again.</Toast>);
      });
  };

  const handleDeletePhoto = async ({
    photoIdAtSlidePosition,
    onComplete,
  }: {
    photoIdAtSlidePosition: string;
    onComplete: () => void;
  }) => {
    try {
      if (saveViaMultiplex) {
        await multiplexDeletePhotoAsync({
          postData: {
            multiplexBuildingSessionId: multiplexBuildingSessionId as string,
            photoId: photoIdAtSlidePosition,
          },
        });
      } else if (photoContext.type === 'property') {
        await deletePropertyPhoto({ postData: { photoId: photoIdAtSlidePosition } });
      } else if (isFeed) {
        await deleteFeedFloorPlanPhoto({
          postData: {
            listingAlias,
            floorPlanId: photoContext.floorPlanId,
            source: photos.find((photo) => photo.id === photoIdAtSlidePosition)?.source ?? '',
          },
        });
      } else {
        await deleteFloorPlanPhoto({ postData: { photoId: photoIdAtSlidePosition } });
      }

      setPhotos((prev) => prev.filter((photo) => photo.id !== photoIdAtSlidePosition));
      onPhotoDeleted?.(photoIdAtSlidePosition);

      setClickedPhotoIdx(null);

      if (!isMultifamily) {
        enqueueToast(<Toast tone="success">Photo deleted</Toast>);
      }
    } catch (err) {
      enqueueToast(
        <Toast tone="critical">
          {err.message || 'There was an error with deleting the photo, refresh the page to try again'}
        </Toast>,
      );
    } finally {
      setTimeout(() => {
        onComplete();
      }, 0);
    }
  };

  const handleCaptionUpdate = async ({
    photoId,
    caption,
    onComplete,
  }: {
    photoId: string;
    caption: string;
    onComplete: () => void;
  }) => {
    const captionSaveContext: CaptionSaveUpdateContext = {
      photos,
      photoId,
      caption,
      setPhotos,
      discriminatoryContentErrors,
      setDiscriminatoryContentErrors,
    };

    try {
      if (saveViaMultiplex) {
        const result = await multiplexPhotoCaptionAsync({
          postData: {
            multiplexBuildingSessionId: multiplexBuildingSessionId as string,
            photoId,
            caption,
          },
        });

        if (!result.success) {
          applyCaptionSaveError(captionSaveContext, result.errorMessage);
        } else {
          applyCaptionSaveSuccess(captionSaveContext);
        }
      } else if (photoContext.type === 'property') {
        await updatePropertyPhotoCaption({ postData: { photoId, caption } });
      } else {
        await updateFloorPlanPhotoCaption({ postData: { photoId, caption } });
      }
    } catch (err) {
      const message = (err as { error?: { message?: string } })?.error?.message;
      if (message) {
        applyCaptionSaveError(captionSaveContext, message);
      }
    } finally {
      setTimeout(() => {
        onComplete();
      }, 0);
    }
  };

  const handleClearError = ({ photoAtSlidePosition }: { photoAtSlidePosition: PhotoProps }) => {
    if (photoAtSlidePosition.error) {
      const copyPhotos = cloneDeep(photos);
      const indexToUpdate = copyPhotos.findIndex((copyPhoto) => copyPhoto.id === photoAtSlidePosition.id);

      copyPhotos[indexToUpdate].error = undefined;
      setPhotos(copyPhotos);

      const copyDiscriminatoryContent = cloneDeep(discriminatoryContentErrors);
      const errorIndexToUpdate = copyDiscriminatoryContent.findIndex(
        (error) => error.photoId === photoAtSlidePosition.id,
      );
      copyDiscriminatoryContent.splice(errorIndexToUpdate, 1);

      setDiscriminatoryContentErrors(copyDiscriminatoryContent);
    }
  };

  if (!photos) {
    return null;
  }

  const isPhotoEditBlocked =
    Boolean(uploadingCount > 0) ||
    !canUpdateListing ||
    isArchived ||
    isUpdateListingRestricted ||
    isPhotoUploadRestricted;
  // On feed floor plans only photos uploaded in Rental Manager can be removed; feed-provided photos stay locked.
  const canDeleteFeedFloorPlanPhoto = getFeedFloorPlanPhotoDeleteCheck({
    isFeedFloorPlan: photoContext.type === 'floorPlan' && isFeed,
    isUploadAllowed,
    isPhotoEditBlocked,
  });

  return (
    <div data-testid="PhotoGallery">
      {isUploadAllowed && <FeedPhotoUploadPausedMessage listingAlias={listingAlias} scope={photoContext} />}
      <Box role="status">
        {isFeedReorderPending && (
          <Box css={{ display: 'flex', alignItems: 'center', gap: '200', marginBottom: '400' }}>
            <Spinner size="sm" title={null} />
            <Paragraph>Saving the new photo order…</Paragraph>
          </Box>
        )}
      </Box>
      {!isDragDisabled && <Paragraph css={{ marginBottom: '400' }}>Drag and drop to reorder</Paragraph>}

      {discriminatoryContentErrors.length > 0 && (
        <DiscriminatoryContentErrorAlert errors={discriminatoryContentErrors} />
      )}

      <Box
        css={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
          columnGap: '400',
          rowGap: '400',
        }}
      >
        {isUploadAllowed && (
          <Box>
            <PhotoUploadDropzone
              photoContext={photoContext}
              isSmallDropzone={true}
              hasFixedEmptySize={false}
              smallDisplayText={phoneUploadTrigger != null ? PHOTO_UPLOAD_FROM_COMPUTER_TEXT : undefined}
              setUploadingCount={setUploadingCount}
              uploadingCount={uploadingCount}
              onUploadSuccess={onUploadSuccess}
            />
          </Box>
        )}

        {isUploadAllowed && phoneUploadTrigger != null && <Box>{phoneUploadTrigger}</Box>}

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={photos} strategy={rectSortingStrategy}>
            <List.Root
              appearance="number"
              aria-label="property photos"
              css={{ listStyleType: 'none', paddingInlineStart: 0, display: 'contents' }}
            >
              {photos.map((photo: PhotoProps, idx: number) => (
                <SortableItem
                  key={photo.id}
                  errors={discriminatoryContentErrors}
                  handlePhotoClick={() => setClickedPhotoIdx(idx)}
                  photo={photo}
                  index={idx}
                  isDisabled={!!uploadingCount}
                  isSortDisabled={isFeedReorderPending}
                  isRunningInProd={isRunningInProd}
                  isDigitallyAlteredBadgeEnabled={isAiImageComplianceEnabled}
                />
              ))}
              {uploadingCount > 0 &&
                Array.from({ length: uploadingCount }).map((_, idx) => (
                  <SortableItem
                    key={`placeholder-photo-${uploadingCount}-${idx}`}
                    isUploading={true}
                    photo={{ id: '' }}
                    index={photos.length + idx}
                    isDisabled={true}
                    isRunningInProd={isRunningInProd}
                  />
                ))}
            </List.Root>
          </SortableContext>
        </DndContext>
      </Box>

      {!!(clickedPhotoIdx || clickedPhotoIdx === 0) && (
        <PhotoDetailsModal
          additionalDisabledChecks={isPhotoEditBlocked || isFeed}
          canDeletePhoto={canDeleteFeedFloorPlanPhoto}
          idx={clickedPhotoIdx}
          onCaptionUpdate={handleCaptionUpdate}
          onClearError={handleClearError}
          onClose={() => setClickedPhotoIdx(null)}
          onDeletePhoto={handleDeletePhoto}
          photos={photos}
          getPhotoUrls={getPhotoUrls}
          isRunningInProd={isRunningInProd}
        />
      )}
    </div>
  );
};

export default PhotoGallery;
